"use client";

import { FC, useEffect, useState } from "react";

import { Label } from "@/components/ui/label";
import { handleFormChange, mergeDateAndTime } from "@/utils/formChange";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { convertToMinutes, generateAvailableTimeSlots } from "@/utils/generateTimeSlots";
import { BaseInput, BaseTextArea } from "@/components/input/CustomInputs";
import DatePicker from "@/components/input/DatePicker";
import axiosInstance from "@/utils/axiosInstance";
import CommonCreate from "@/components/common/Create";
import { Transfer } from "@/interface/entityInterface";
import { useUserContext } from "@/context/UserContext";

interface Props {
  data: Transfer;
  title: string;
  description?: string;
}

const BookTransfer: FC<Props> = ({ data }) => {
  const { decodedToken } = useUserContext();  
  const [formProp, setFormProp] = useState({
    fullName: "",
    client_phone: "",
    date: new Date(),
    time: "",
    how_many_adults: 0,
    how_many_children: 0,
    flight_number: "",
    additional_notes: "",
    requested_vehicle_class: "",
    passengers: 0,
  });
  const [comissionPartnerId, setId] = useState(0);

  useEffect(() => {
    if (!decodedToken?.user.id) return;
    const unsub = async () => {
      try {
        const response = await axiosInstance.get(
          `/referral-agents/users/${decodedToken.user.id}`,
        );
        setId(response.data.id);
      } catch (error) {
        console.error("Failed to fetch commission partner:", error);
      }
    };

    unsub();
  }, [decodedToken?.user.id]);

  return (
    <CommonCreate
      title="Book Now"
      resource="taxi-orders/tms"
      data={{
        fixed_fee: true,
        pickup_time: mergeDateAndTime(formProp.date, formProp.time),
        status: "NEW_ORDER",
        email: decodedToken?.user.email,
        pickup: data?.pickup_location,
        dropoff: data?.dropoff_location,
        pickup_latitude: data?.pickup_latitude,
        pickup_longitude: data?.pickup_longitude,
        dropoff_latitude: data?.dropoff_latitude,
        dropoff_longitude: data?.dropoff_longitude,
        client_name: formProp.fullName,
        client_phone: "" + formProp.client_phone,
        auto_assign: false,
        scheduled: false,
        adults: +formProp.how_many_adults,
        children: +formProp.how_many_children,
        currency: data?.currency,
        passengers_number: Number(formProp.passengers),
        order_type: "transfer",
        fare: data?.price,
        transfer_id: data?.id,
        commission_partner_id: comissionPartnerId,
        notes: formProp.additional_notes,
        flight_number: formProp.flight_number,
        requested_vehicle_class: data.vehicle_class,
        total_distance_km: data?.distance,
        estimated_duration_minutes: convertToMinutes(data.duration)
      }}
      responseMessage="Transfer succesfully booked"
    >
      <div className="grid sm:grid-cols-2 place-items-center gap-4">
        <BaseInput
          id="fullName"
          label="Full Name"
          value={formProp.fullName}
          setProp={setFormProp}
          placeholder="Enter full name"
        />
        <BaseInput
          id="client_phone"
          label="Phone number"
          value={formProp.client_phone}
          setProp={setFormProp}
          placeholder="Phone number"
          type="number"
        />
        <div className="w-full">
          <Label htmlFor="date" className="text-right mb-2">
            Date *
          </Label>
          <DatePicker
            value={formProp.date}
            setValue={(e: any) => handleFormChange(e, setFormProp)}
          />
        </div>
        <div className="w-full">
          <Label htmlFor="time" className="text-right mb-2">
            Time *
          </Label>
          <Select
            value={formProp.time}
            onValueChange={(value) =>
              setFormProp((prev: any) => ({ ...prev, time: value }))
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent>
              {generateAvailableTimeSlots(formProp.date).map((time) => (
                <SelectItem key={time} value={time}>
                  {time}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <BaseInput
            id="flight_number"
            label="Flight number"
            value={formProp.flight_number}
            setProp={setFormProp}
            required={false}
            placeholder="Enter flight number"
          />
        </div>
        <div className="w-full sm:col-span-2">
          <BaseTextArea
            id="additional_notes"
            label="Notes"
            value={formProp.additional_notes}
            setProp={setFormProp}
            required={false}
          />
        </div>
      </div>
    </CommonCreate>
  );
};

export default BookTransfer;
